import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import type { Academy, Profile } from '@/lib/supabaseClient';
import { useProfile } from '@/hooks/useProfile';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import Modal from '@/components/base/Modal';
import RegionSelect from '@/components/base/RegionSelect';

export default function Academies() {
  const { profile, loading: profileLoading } = useProfile();
  const [academies, setAcademies] = useState<Academy[]>([]);
  const [members, setMembers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 계정/학원 삭제
  const [target, setTarget] = useState<Profile | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [targetAcademy, setTargetAcademy] = useState<Academy | null>(null);
  const [deletingAcademy, setDeletingAcademy] = useState(false);

  // 학원 수정
  const [editingAcademy, setEditingAcademy] = useState<Academy | null>(null);
  const [academyName, setAcademyName] = useState('');
  const [academyAddress, setAcademyAddress] = useState('');
  const [savingAcademy, setSavingAcademy] = useState(false);

  // 구성원 수정
  const [editingMember, setEditingMember] = useState<Profile | null>(null);
  const [memberName, setMemberName] = useState('');
  const [memberAddress, setMemberAddress] = useState('');
  const [savingMember, setSavingMember] = useState(false);
  const [memberAcademyId, setMemberAcademyId] = useState('');

  // 학원 생성
  const [creatingAcademy, setCreatingAcademy] = useState(false);
  const [newAcademyName, setNewAcademyName] = useState('');
  const [newAcademyAddress, setNewAcademyAddress] = useState('');
  const [savingNewAcademy, setSavingNewAcademy] = useState(false);

  const isOperator = profile?.role === 'operator';

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [academiesRes, membersRes] = await Promise.all([
        supabase.from('academies').select('*').order('created_at', { ascending: true }),
        supabase
          .from('profiles')
          .select('*')
          .in('role', ['owner', 'teacher'])
          .eq('status', 'approved')
          .order('created_at', { ascending: true }),
      ]);
      if (academiesRes.error) throw academiesRes.error;
      if (membersRes.error) throw membersRes.error;
      setAcademies((academiesRes.data as Academy[]) || []);
      setMembers((membersRes.data as Profile[]) || []);
    } catch (e) {
      setError('학원 목록을 불러오지 못했어요.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOperator) loadData();
  }, [isOperator, loadData]);

  const membersByAcademy = useMemo(() => {
    const map: Record<string, Profile[]> = {};
    members.forEach((m) => {
      if (!m.academy_id) return;
      if (!map[m.academy_id]) map[m.academy_id] = [];
      map[m.academy_id].push(m);
    });
    return map;
  }, [members]);

  const unassigned = useMemo(() => members.filter((m) => !m.academy_id), [members]);

  async function handleDelete() {
    if (!target) return;
    setDeleting(true);
    const { error: err } = await supabase.rpc('delete_account', { p_user_id: target.id });
    setDeleting(false);
    setTarget(null);
    if (err) {
      setError('계정 삭제 중 문제가 발생했어요. 다시 시도해 주세요.');
      console.error(err);
      return;
    }
    loadData();
  }

  async function handleDeleteAcademy() {
    if (!targetAcademy) return;
    setDeletingAcademy(true);
    const { error: err } = await supabase.rpc('delete_academy', {
      p_academy_id: targetAcademy.id,
    });
    setDeletingAcademy(false);
    setTargetAcademy(null);
    if (err) {
      setError('학원 삭제 중 문제가 발생했어요. 다시 시도해 주세요.');
      console.error(err);
      return;
    }
    loadData();
  }

  function openAcademyEdit(a: Academy) {
    setEditingAcademy(a);
    setAcademyName(a.name || '');
    setAcademyAddress(a.address || '');
  }

  async function handleSaveAcademy() {
    if (!editingAcademy) return;
    if (!academyName.trim()) {
      setError('학원 이름을 입력해 주세요.');
      return;
    }
    setSavingAcademy(true);
    const { error: err } = await supabase
      .from('academies')
      .update({ name: academyName.trim(), address: academyAddress.trim() })
      .eq('id', editingAcademy.id);
    setSavingAcademy(false);
    if (err) {
      setError('학원 정보 수정 중 문제가 발생했어요. 다시 시도해 주세요.');
      console.error(err);
      return;
    }
    setEditingAcademy(null);
    loadData();
  }

  function openMemberEdit(m: Profile) {
    setEditingMember(m);
    setMemberName(m.full_name || '');
    setMemberAddress(m.address || '');
    setMemberAcademyId(m.academy_id || '');
  }

  async function handleSaveMember() {
    if (!editingMember) return;
    if (!memberName.trim()) {
      setError('이름을 입력해 주세요.');
      return;
    }
    setSavingMember(true);
    const selectedAcademy = academies.find((a) => a.id === memberAcademyId);
    const { error: err } = await supabase
      .from('profiles')
      .update({
        full_name: memberName.trim(),
        address: memberAddress.trim(),
        academy_id: memberAcademyId || null,
        academy_name: selectedAcademy?.name || null,
      })
      .eq('id', editingMember.id);
    setSavingMember(false);
    if (err) {
      setError('구성원 정보 수정 중 문제가 발생했어요. 다시 시도해 주세요.');
      console.error(err);
      return;
    }
    setEditingMember(null);
    loadData();
  }

  async function handleCreateAcademy() {
    if (!newAcademyName.trim()) {
      setError('학원 이름을 입력해 주세요.');
      return;
    }
    setSavingNewAcademy(true);
    const { error: err } = await supabase
      .from('academies')
      .insert({ name: newAcademyName.trim(), address: newAcademyAddress.trim() });
    setSavingNewAcademy(false);
    if (err) {
      setError('학원 생성 중 문제가 발생했어요. 다시 시도해 주세요.');
      console.error(err);
      return;
    }
    setCreatingAcademy(false);
    setNewAcademyName('');
    setNewAcademyAddress('');
    loadData();
  }

  if (profileLoading || loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-2xl text-foreground-500"></i>
      </div>
    );
  }

  if (!isOperator) {
    return (
      <div className="mx-auto flex max-w-lg flex-col items-center justify-center py-24 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-accent-700">
          <i className="ri-lock-line text-3xl"></i>
        </div>
        <h2 className="mt-4 text-lg font-bold text-foreground-950">접근 권한이 없어요</h2>
        <p className="mt-1 text-sm text-foreground-500">학원 관리는 시스템 운영자만 이용할 수 있어요.</p>
      </div>
    );
  }

  const totalOwners = members.filter((m) => m.role === 'owner').length;
  const totalTeachers = members.filter((m) => m.role === 'teacher').length;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground-950">학원 관리</h2>
          <p className="mt-1 text-sm text-foreground-500">
            가입된 학원과 원장·선생님 구성원의 이름·지역·소속 학원을 수정하고, 학원을 생성하거나 계정을 삭제할 수 있어요.
          </p>
        </div>
        <button
          onClick={() => setCreatingAcademy(true)}
          className="flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-add-line"></i>
          학원 생성
        </button>
      </div>

      {error && (
        <div className="flex items-center justify-between rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-800">
          <span>{error}</span>
          <button
            onClick={loadData}
            className="cursor-pointer whitespace-nowrap font-semibold underline"
          >
            다시 시도
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-background-200 bg-white p-5">
          <p className="text-sm text-foreground-500">전체 학원</p>
          <p className="mt-2 text-2xl font-bold text-foreground-950">{academies.length}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-white p-5">
          <p className="text-sm text-foreground-500">전체 원장</p>
          <p className="mt-2 text-2xl font-bold text-foreground-950">{totalOwners}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-white p-5">
          <p className="text-sm text-foreground-500">전체 선생님</p>
          <p className="mt-2 text-2xl font-bold text-foreground-950">{totalTeachers}</p>
        </div>
      </div>

      {academies.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-white py-16 text-center">
          <i className="ri-building-2-line text-4xl text-foreground-300"></i>
          <p className="mt-3 text-sm text-foreground-500">아직 가입된 학원이 없어요.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {academies.map((a) => {
            const acMembers = membersByAcademy[a.id] || [];
            const owner = acMembers.find((m) => m.role === 'owner');
            const teachers = acMembers.filter((m) => m.role === 'teacher');
            return (
              <div key={a.id} className="rounded-lg border border-background-200 bg-white p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                        <i className="ri-building-2-line"></i>
                      </span>
                      <h3 className="text-base font-bold text-foreground-950">{a.name}</h3>
                      <button
                        onClick={() => openAcademyEdit(a)}
                        className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-900"
                        title="학원 정보 수정"
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                    </div>
                    {a.address && (
                      <p className="mt-2 flex items-start gap-1.5 text-xs text-foreground-500">
                        <i className="ri-map-pin-line mt-0.5"></i>
                        {a.address}
                      </p>
                    )}
                  </div>
                  <span className="text-xs text-foreground-400">
                    개설일 {a.created_at?.slice(0, 10) || '-'}
                  </span>
                </div>

                {/* 원장 */}
                <div className="mt-4 rounded-md bg-background-50 p-3">
                  <p className="mb-2 text-xs font-semibold text-foreground-500">원장</p>
                  {owner ? (
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-500 text-background-50">
                          <span className="text-sm font-bold">{(owner.full_name || '이').charAt(0)}</span>
                        </span>
                        <div className="min-w-0">
                          <span className="block truncate text-sm font-medium text-foreground-900">
                            {owner.full_name || '이름 없음'}
                          </span>
                          {owner.address && (
                            <span className="block truncate text-xs text-foreground-500">
                              {owner.address}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1.5">
                        <button
                          onClick={() => openMemberEdit(owner)}
                          className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-2.5 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:bg-background-100"
                        >
                          수정
                        </button>
                        <button
                          onClick={() => setTarget(owner)}
                          className="cursor-pointer whitespace-nowrap rounded-md border border-accent-200 px-2.5 py-1.5 text-xs font-medium text-accent-700 transition-colors hover:bg-accent-100"
                        >
                          탈퇴
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-foreground-400">원장이 배정되지 않았어요.</p>
                  )}
                </div>

                {/* 선생님 */}
                <div className="mt-3">
                  <p className="mb-2 text-xs font-semibold text-foreground-500">
                    선생님 {teachers.length}명
                  </p>
                  {teachers.length === 0 ? (
                    <p className="text-xs text-foreground-400">배정된 선생님이 없어요.</p>
                  ) : (
                    <div className="space-y-2">
                      {teachers.map((t) => (
                        <div
                          key={t.id}
                          className="flex items-center justify-between gap-2 rounded-md border border-background-100 px-3 py-2"
                        >
                          <div className="flex min-w-0 items-center gap-2.5">
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary-500 text-background-50">
                              <span className="text-xs font-bold">{(t.full_name || '이').charAt(0)}</span>
                            </span>
                            <div className="min-w-0">
                              <span className="block truncate text-sm text-foreground-800">
                                {t.full_name || '이름 없음'}
                              </span>
                              {t.address && (
                                <span className="block truncate text-xs text-foreground-500">
                                  {t.address}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex shrink-0 items-center gap-1.5">
                            <button
                              onClick={() => openMemberEdit(t)}
                              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-2.5 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:bg-background-100"
                            >
                              수정
                            </button>
                            <button
                              onClick={() => setTarget(t)}
                              className="cursor-pointer whitespace-nowrap rounded-md border border-accent-200 px-2.5 py-1.5 text-xs font-medium text-accent-700 transition-colors hover:bg-accent-100"
                            >
                              탈퇴
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* 학원 삭제 */}
                <div className="mt-4 border-t border-background-100 pt-3">
                  <button
                    onClick={() => setTargetAcademy(a)}
                    className="flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-accent-300 px-3 py-2 text-sm font-medium text-accent-700 transition-colors hover:bg-accent-50"
                  >
                    <i className="ri-delete-bin-6-line"></i>
                    학원 삭제
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 학원 미배정 계정 */}
      {unassigned.length > 0 && (
        <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
          <div className="flex flex-wrap items-center gap-2">
            <i className="ri-alert-line text-accent-600"></i>
            <h3 className="text-base font-bold text-foreground-950">학원 미배정 계정</h3>
            <span className="rounded-full bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-700">
              {unassigned.length}명
            </span>
          </div>
          <p className="mt-1 text-xs text-foreground-500">
            학원에 배정되지 않은 계정이에요. 필요시 수정하거나 삭제할 수 있어요.
          </p>
          <div className="mt-3 space-y-2">
            {unassigned.map((m) => (
              <div
                key={m.id}
                className="flex items-center justify-between gap-2 rounded-md border border-background-100 bg-white px-3 py-2"
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                      m.role === 'owner'
                        ? 'bg-accent-500 text-background-50'
                        : 'bg-secondary-500 text-background-50'
                    }`}
                  >
                    <span className="text-sm font-bold">{(m.full_name || '이').charAt(0)}</span>
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-foreground-900">
                        {m.full_name || '이름 없음'}
                      </span>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                          m.role === 'owner'
                            ? 'bg-accent-100 text-accent-800'
                            : 'bg-secondary-100 text-secondary-700'
                        }`}
                      >
                        {m.role === 'owner' ? '원장님' : '선생님'}
                      </span>
                    </div>
                    {m.address && (
                      <span className="block truncate text-xs text-foreground-500">
                        {m.address}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <button
                    onClick={() => openMemberEdit(m)}
                    className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-2.5 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:bg-background-100"
                  >
                    수정
                  </button>
                  <button
                    onClick={() => setTarget(m)}
                    className="cursor-pointer whitespace-nowrap rounded-md border border-accent-200 px-2.5 py-1.5 text-xs font-medium text-accent-700 transition-colors hover:bg-accent-100"
                  >
                    탈퇴
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 계정 삭제 */}
      <ConfirmDialog
        open={!!target}
        title="계정 삭제"
        message={`'${target?.full_name || '이 회원'}'님의 계정을 완전히 삭제할까요? 로그인이 불가능해지며, 이 작업은 되돌릴 수 없어요.`}
        confirmLabel="계정 삭제"
        loading={deleting}
        onCancel={() => setTarget(null)}
        onConfirm={handleDelete}
      />

      {/* 학원 삭제 */}
      <ConfirmDialog
        open={!!targetAcademy}
        title="학원 삭제"
        message={`'${targetAcademy?.name || '이 학원'}' 학원을 완전히 삭제할까요? 학원의 모든 데이터(학생·반·출결·시험지)와 함께 원장·선생님 계정까지 전부 삭제되고, 이 작업은 되돌릴 수 없어요.`}
        confirmLabel="전체 삭제"
        loading={deletingAcademy}
        dangerConfirmText={targetAcademy?.name || ''}
        onCancel={() => setTargetAcademy(null)}
        onConfirm={handleDeleteAcademy}
      />

      {/* 학원 수정 */}
      <Modal
        open={!!editingAcademy}
        title="학원 정보 수정"
        onClose={() => setEditingAcademy(null)}
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-800">학원 이름</label>
            <input
              value={academyName}
              onChange={(e) => setAcademyName(e.target.value)}
              placeholder="예: 대구달서수학학원"
              className="w-full rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-800">지역 / 주소</label>
            <RegionSelect value={academyAddress} onChange={setAcademyAddress} />
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setEditingAcademy(null)}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100"
            >
              취소
            </button>
            <button
              onClick={handleSaveAcademy}
              disabled={savingAcademy}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {savingAcademy ? '저장 중...' : '저장'}
            </button>
          </div>
        </div>
      </Modal>

      {/* 구성원 수정 */}
      <Modal
        open={!!editingMember}
        title="구성원 정보 수정"
        onClose={() => setEditingMember(null)}
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-800">이름</label>
            <input
              value={memberName}
              onChange={(e) => setMemberName(e.target.value)}
              placeholder="예: 김철수"
              className="w-full rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-800">소속 학원</label>
            <select
              value={memberAcademyId}
              onChange={(e) => setMemberAcademyId(e.target.value)}
              className="w-full cursor-pointer rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            >
              <option value="">학원 미배정</option>
              {academies.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-800">지역 / 주소</label>
            <RegionSelect value={memberAddress} onChange={setMemberAddress} />
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setEditingMember(null)}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100"
            >
              취소
            </button>
            <button
              onClick={handleSaveMember}
              disabled={savingMember}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {savingMember ? '저장 중...' : '저장'}
            </button>
          </div>
        </div>
      </Modal>

      {/* 학원 생성 */}
      <Modal
        open={creatingAcademy}
        title="학원 생성"
        onClose={() => setCreatingAcademy(false)}
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-800">학원 이름</label>
            <input
              value={newAcademyName}
              onChange={(e) => setNewAcademyName(e.target.value)}
              placeholder="예: g1230수학학원 세천점"
              className="w-full rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-800">지역 / 주소</label>
            <RegionSelect value={newAcademyAddress} onChange={setNewAcademyAddress} />
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setCreatingAcademy(false)}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100"
            >
              취소
            </button>
            <button
              onClick={handleCreateAcademy}
              disabled={savingNewAcademy}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {savingNewAcademy ? '생성 중...' : '생성'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}